import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { IconBuilding } from '../../components/Icons';
import { toApiError } from '../../api/client';
import { businessApi } from '../../api/endpoints';
import type { AccommodationCategory, Business, Member } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { Checkbox, ConfirmDialog, Field, PageHeader } from '../../components/Common';
import { EmptyState, InlineError, InlineSuccess, TableSkeleton } from '../../components/States';
import { categoryLabel } from '../../utils/format';

const ALL_CATEGORIES: AccommodationCategory[] = ['PgHostel', 'HomeRental', 'Hotel'];

export function OwnerBusinessesPage() {
  const { refreshProfile } = useAuth();

  const [businesses, setBusinesses] = useState<Business[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<Business | 'new' | null>(null);
  const [managingTeam, setManagingTeam] = useState<Business | null>(null);
  const [archiving, setArchiving] = useState<Business | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);

    try {
      setBusinesses(await businessApi.list());
    } catch (err) {
      setError(toApiError(err).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const archive = async () => {
    if (!archiving) return;

    setBusy(true);
    setError(null);

    try {
      await businessApi.archive(archiving.id);
      setSuccess(`${archiving.name} has been archived.`);
      setArchiving(null);
      await load();
      await refreshProfile();
    } catch (err) {
      setError(toApiError(err).message);
      setArchiving(null);
    } finally {
      setBusy(false);
    }
  };

  if (!businesses && !error) return <TableSkeleton rows={3} columns={4} />;

  return (
    <>
      <PageHeader
        title="My businesses"
        description="Each business owns its own branches, staff and billing."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            Add a business
          </button>
        }
      />

      <InlineError message={error} />
      <InlineSuccess message={success} />

      {businesses && businesses.length === 0 && (
        <EmptyState
          icon={<IconBuilding />}
          title="No businesses yet"
          description="A business is your brand. Branches and listings sit underneath it."
          action={
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              Create your first business
            </button>
          }
        />
      )}

      <div className="row g-3">
        {businesses?.map((business) => (
          <div className="col-12" key={business.id}>
            <div className="sf-card sf-card-pad">
              <div className="d-flex flex-wrap justify-content-between gap-2">
                <div>
                  <h3 className="h6 mb-1">
                    {business.name}{' '}
                    {business.isVerified && <span className="sf-pill sf-pill--ok">Verified</span>}
                  </h3>

                  <p className="sf-muted small mb-2">
                    {business.contactPhone}
                    {business.contactEmail && <> · {business.contactEmail}</>}
                    {' · '}
                    {business.publishedPropertyCount} of {business.propertyCount} published
                  </p>

                  <div className="d-flex flex-wrap gap-2">
                    {business.categories.map((category) => (
                      <span className="sf-chip" key={category}>
                        {categoryLabel(category)}
                      </span>
                    ))}

                    {ALL_CATEGORIES.filter((c) => !business.categories.includes(c)).map((category) => (
                      <button
                        type="button"
                        key={category}
                        className="btn btn-sm btn-outline-secondary py-0"
                        onClick={async () => {
                          try {
                            await businessApi.addCategory(business.id, category);
                            setSuccess(`${categoryLabel(category)} added to ${business.name}.`);
                            await load();
                            await refreshProfile();
                          } catch (err) {
                            setError(toApiError(err).message);
                          }
                        }}
                      >
                        + {categoryLabel(category)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="d-flex flex-column gap-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(business)}>
                    Edit
                  </button>
                  {business.myRole === 'Owner' && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setManagingTeam(business)}
                      >
                        Team access
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setArchiving(business)}
                      >
                        Archive
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <BusinessDialog
          business={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (message) => {
            setEditing(null);
            setSuccess(message);
            await load();
            await refreshProfile();
          }}
        />
      )}

      {managingTeam && <TeamDialog business={managingTeam} onClose={() => setManagingTeam(null)} />}

      <ConfirmDialog
        open={archiving !== null}
        title="Archive this business?"
        destructive
        busy={busy}
        confirmLabel="Archive"
        message={
          <>
            <p>
              <strong>{archiving?.name}</strong> will be hidden from your portal. Its history is kept, and it can be
              restored by support.
            </p>
            <p className="mb-0 sf-muted small">
              A business that still has published listings cannot be archived — let them expire or archive them first.
            </p>
          </>
        }
        onConfirm={archive}
        onCancel={() => setArchiving(null)}
      />
    </>
  );
}

function BusinessDialog({
  business,
  onClose,
  onSaved,
}: {
  business: Business | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(business?.name ?? '');
  const [contactPhone, setContactPhone] = useState(business?.contactPhone ?? '');
  const [contactEmail, setContactEmail] = useState(business?.contactEmail ?? '');
  const [contactPersonName, setContactPersonName] = useState(business?.contactPersonName ?? '');
  const [whatsAppNumber, setWhatsAppNumber] = useState(business?.whatsAppNumber ?? '');
  const [description, setDescription] = useState(business?.description ?? '');
  const [taxRegistrationNumber, setTax] = useState(business?.taxRegistrationNumber ?? '');
  const [categories, setCategories] = useState<string[]>(business?.categories ?? ['PgHostel']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const body = {
      name: name.trim(),
      contactPhone: contactPhone.trim(),
      contactEmail: contactEmail.trim() || undefined,
      contactPersonName: contactPersonName.trim() || undefined,
      whatsAppNumber: whatsAppNumber.trim() || undefined,
      description: description.trim() || undefined,
      taxRegistrationNumber: taxRegistrationNumber.trim() || undefined,
      categories,
    };

    try {
      if (business) {
        await businessApi.update(business.id, body);
        onSaved(`${body.name} updated.`);
      } else {
        await businessApi.create(body);
        onSaved(`${body.name} created.`);
      }
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="business-dialog-title">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <form onSubmit={submit} noValidate>
              <div className="modal-header">
                <h2 className="modal-title h5" id="business-dialog-title">
                  {business ? 'Edit business' : 'Add a business'}
                </h2>
              </div>

              <div className="modal-body">
                <InlineError message={error} />

                <Field label="Business or brand name" htmlFor="biz-name" required>
                  <input
                    id="biz-name"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={200}
                  />
                </Field>

                <div className="row">
                  <div className="col-12 col-md-6">
                    <Field label="Contact phone" htmlFor="biz-phone" required>
                      <input
                        id="biz-phone"
                        className="form-control"
                        type="tel"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        required
                      />
                    </Field>
                  </div>
                  <div className="col-12 col-md-6">
                    <Field label="WhatsApp number" htmlFor="biz-whatsapp" hint="Defaults to the contact phone.">
                      <input
                        id="biz-whatsapp"
                        className="form-control"
                        type="tel"
                        value={whatsAppNumber}
                        onChange={(e) => setWhatsAppNumber(e.target.value)}
                      />
                    </Field>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 col-md-6">
                    <Field label="Contact person" htmlFor="biz-person">
                      <input
                        id="biz-person"
                        className="form-control"
                        value={contactPersonName}
                        onChange={(e) => setContactPersonName(e.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-12 col-md-6">
                    <Field label="Contact email" htmlFor="biz-email">
                      <input
                        id="biz-email"
                        className="form-control"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                      />
                    </Field>
                  </div>
                </div>

                <Field label="GSTIN or registration number" htmlFor="biz-tax" hint="Used on your payment receipts.">
                  <input
                    id="biz-tax"
                    className="form-control"
                    value={taxRegistrationNumber}
                    onChange={(e) => setTax(e.target.value)}
                    maxLength={30}
                  />
                </Field>

                <Field label="About this business" htmlFor="biz-description">
                  <textarea
                    id="biz-description"
                    className="form-control"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={4000}
                  />
                </Field>

                <fieldset>
                  <legend className="form-label">Categories</legend>
                  {ALL_CATEGORIES.map((category) => (
                    <Checkbox
                      key={category}
                      id={`biz-cat-${category}`}
                      label={categoryLabel(category)}
                      checked={categories.includes(category)}
                      onChange={(checked) =>
                        setCategories((previous) =>
                          checked ? [...previous, category] : previous.filter((c) => c !== category),
                        )
                      }
                    />
                  ))}
                  {business && (
                    <div className="form-text">
                      Categories can only be added here. Removing one would orphan its listings.
                    </div>
                  )}
                </fieldset>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

function TeamDialog({ business, onClose }: { business: Business; onClose: () => void }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [email, setEmail] = useState('');
  const [canManageProperties, setCanManageProperties] = useState(true);
  const [canPublishListings, setCanPublishListings] = useState(false);
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setMembers(await businessApi.members(business.id));
    } catch (err) {
      setError(toApiError(err).message);
    }
  }, [business.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      await businessApi.addMember(business.id, {
        email: email.trim(),
        canManageProperties,
        canPublishListings,
        canManageBilling,
      });

      setSuccess(`${email.trim()} now has access to ${business.name}.`);
      setEmail('');
      await load();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (membershipId: string) => {
    try {
      await businessApi.removeMember(business.id, membershipId);
      await load();
    } catch (err) {
      setError(toApiError(err).message);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="team-dialog-title">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="team-dialog-title">
                Team access · {business.name}
              </h2>
            </div>

            <div className="modal-body">
              <InlineError message={error} />
              <InlineSuccess message={success} />

              <p className="sf-muted small">
                A manager can only see and edit this business. They cannot reach your other businesses.
              </p>

              {members && members.length > 0 && (
                <div className="sf-table-wrap mb-3">
                  <table className="sf-table">
                    <thead>
                      <tr>
                        <th scope="col">Person</th>
                        <th scope="col">Role</th>
                        <th scope="col">Can</th>
                        <th scope="col" />
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.id}>
                          <td>
                            {member.fullName}
                            <div className="sf-muted small">{member.email}</div>
                          </td>
                          <td>{member.role}</td>
                          <td className="small">
                            {[
                              member.canManageProperties && 'edit listings',
                              member.canPublishListings && 'publish',
                              member.canManageBilling && 'pay',
                            ]
                              .filter(Boolean)
                              .join(', ') || '—'}
                          </td>
                          <td>
                            {member.role !== 'Owner' && member.isActive && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => remove(member.id)}
                              >
                                Remove
                              </button>
                            )}
                            {!member.isActive && <span className="sf-muted small">Removed</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <form onSubmit={add} noValidate>
                <h3 className="sf-section-title">Add a manager</h3>

                <Field
                  label="Their email address"
                  htmlFor="member-email"
                  required
                  hint="They need a RoomKart account already."
                >
                  <input
                    id="member-email"
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Field>

                <Checkbox
                  id="perm-properties"
                  label="Add and edit listings"
                  checked={canManageProperties}
                  onChange={setCanManageProperties}
                />
                <Checkbox
                  id="perm-publish"
                  label="Submit listings for publication"
                  checked={canPublishListings}
                  onChange={setCanPublishListings}
                />
                <Checkbox
                  id="perm-billing"
                  label="Make payments and renew subscriptions"
                  checked={canManageBilling}
                  onChange={setCanManageBilling}
                />

                <button type="submit" className="btn btn-primary mt-2" disabled={busy}>
                  {busy ? 'Adding…' : 'Add manager'}
                </button>
              </form>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
